import { EinsteinEntryIconRaw } from '@tableau/icons/EinsteinEntryIcon/raw';
import { ExtensionApiIconRaw } from '@tableau/icons/ExtensionApiIcon/raw';
import { ReactiveRawIconRegistry } from '@tableau/icons/reactive';
import { TrashBaseIcon } from '@tableau/icons/TrashBaseIcon';

import { SettingsSection } from '~src/settings/components/common/SettingsSection';
import { ExtensionsSettingsSubspaceState } from '~src/settings/extensions-settings/ExtensionsSettingsSubspaceShared';
import { componentRegistryEntry } from '~src/settings/general-settings/components/SettingsComponentRegistryFunctions';
import { applyColor }from '~src/style/ColorService';
import React = require('react');
import DesignTokens from '@tableau/design-tokens';
import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  DialogPositioningBehavior, DialogType,
  InputLabelWidget,
  TextLinkWidget
} from '@tableau/widgets';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { openReactConfirmationDialog } from '~commonInfra/dialogs/ReactConfirmationDialog';
import { ModalInstance, ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { HelpService } from '~commonInfra/services/HelpService';
import { Messages } from '~src/codegen/Localize';
import { imgUrl } from '~src/commonInfra/utils/ImgUrl';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import {
  IAnalyticsExtensionsSettings
} from '~src/settings/controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import {
  AnalyticsExtensionsSettingsDialog,
  IAnalyticsExtensionsDialogProps
} from '~src/settings/extensions-settings/analytics/AnalyticsExtensionsSettingsDialog';
import { AnalyticsExtensionsSettingsSubFormikProps } from '~src/settings/extensions-settings/ExtensionsSettingsCoordinator';
import { AddAnalyticsExtConnectionSettingsRequest } from '~src/settings/extensions-settings/serverApi/requests/analytics/AddAnalyticsExtConnectionSettingsRequest';
import { DeleteAnalyticsExtConnectionSettingsRequest } from '~src/settings/extensions-settings/serverApi/requests/analytics/DeleteAnalyticsExtConnectionSettingsRequest';
import {
  GetAnalyticsExtConnectionSettingsRequest,
  IGetAnalyticsExtConnectionSettingsResult
} from '~src/settings/extensions-settings/serverApi/requests/analytics/GetAnalyticsExtConnectionSettingsRequest';
import { UpdateAnalyticsExtConnectionSettingsRequest } from '~src/settings/extensions-settings/serverApi/requests/analytics/UpdateAnalyticsExtConnectionSettingsRequest';
import { AnalyticsExtensionsSettingsStyles } from '~src/settings/extensions-settings/styles/AnalyticsExtensionsSettingsStyles';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import {
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings
} from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import { getAnalyticsExtensionConnectionIcon } from './AnalyticsExtensionConnectionIcons';

const EINSTEIN_GLYPH_PAD = 4.2; // keeps legacy ~0.68 ink-fill under background-size:contain
function EinsteinEntryPaddedRaw(size: number, color: string): string {
  const inner = EinsteinEntryIconRaw(24, color); // '<svg width="24" height="24" ...>GUTS</svg>'
  const guts = inner.replace(/^<svg[^>]*>/, '').replace(/<\/svg>\s*$/, '');
  const m = EINSTEIN_GLYPH_PAD;
  const P = 24 + 2 * m; // padded square viewBox side
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${P} ${P}" xmlns="http://www.w3.org/2000/svg"><g transform="translate(${m},${m})">${guts}</g></svg>`;
}
EinsteinEntryPaddedRaw.iconName = 'EinsteinEntryPaddedRaw';

const EINSTEIN_DISCOVERY_CELL_ICON_VAR = `var(${ReactiveRawIconRegistry.register(
  EinsteinEntryPaddedRaw,
  24,
  applyColor(DesignTokens.color.font.default.secondary, '#5c5c5c')
)})`;
const ANALYTICS_EXTENSION_API_CELL_ICON_VAR = `var(${ReactiveRawIconRegistry.register(
  ExtensionApiIconRaw,
  24,
  applyColor(DesignTokens.color.font.default.secondary, '#5c5c5c')
)})`;

export const ConnectionTypes = {
  TABPY: {
    TAG: 'TABPY',
    DISPLAY: 'TabPy/Analytics Extension'
  },
  RSERVE: {
    TAG: 'RSERVE',
    DISPLAY: 'Rserve TCP'
  },
  RSERVE_HTTP: {
    TAG: 'RSERVE_HTTP',
    DISPLAY: 'Rserve HTTP'
  },
  EINSTEIN: {
    TAG: 'EINSTEIN_DISCOVERY',
    DISPLAY: 'Einstein Discovery'
  },
  GENERIC_API: {
    TAG: 'GENERIC_API',
    DISPLAY: 'Analytics Extension API'
  },
  SELECT: 'Select'
};

export const defaultAnalyticsConnection: IAnalyticsExtConnectionSettings = {
  connection_brief: {
    connection_type: 'SELECT',
    connection_name: ''
  },
  connection_luid: '',
  host: '',
  port: undefined,
  is_ssl_enabled: false,
  is_auth_enabled: false,
  username: '',
  password: ''
};

export function AnalyticsExtensionsSiteSection(props: AnalyticsExtensionsSettingsSubFormikProps) {
  const [namedConnections, setNamedConnections] = React.useState<IAnalyticsExtConnectionSettings[]>(undefined);
  const [defaultConnection, setDefaultConnection] = React.useState<IAnalyticsExtConnectionSettings>(defaultAnalyticsConnection);

  const sessionManagementService = getSessionManagementServiceInstance();
  const isOnline = sessionManagementService.isOnlineEnvironment();
  const isSingleSiteOnPremServerSetup = !sessionManagementService.doesTheServerHaveMoreThanOneSite() && !isOnline;

  function initializeConnections() {
    getAnalyticsConnections().then((result) => {
      if (!result || result.length === 0) {
        result = [];
        result.push(defaultAnalyticsConnection);
      }
      result.map(connection => connection.connection_brief.connection_type = getConnectionTypeDisplay(connection));
      setDefaultConnection(result[0]);
      setNamedConnections(result);
    });
  }

  React.useEffect(() => initializeConnections(), []);

  function getDialogProps(): IAnalyticsExtensionsDialogProps {
    return {
      initialAnalyticsExtensionsSettings: {
        connectionDefault: defaultConnection,
        connectionSettingsList: namedConnections,
        enableExtSvcSite: props.settings.analyticsExtensionsSiteSettings.enabled
      },
      isOnline: isOnline,
      sendUpdateConnectionRequest: updateAnalyticsExtConnectionSettings,
      sendDeleteRequest: deleteAnExtConnection
    };
  }

  function showHybridDialog(ctrlProps: IAnalyticsExtensionsDialogProps, connectionLUID: string) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      connectionLUID,
      ctrlProps: ctrlProps,
      // if serverSettings is undefined, we are in online which means savedAccessTokensEnabled should be true
      savedAccessTokensEnabled: this.serverSettings ? this.serverSettings.savedAccessTokensEnabled : true
    };

    modalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: connectionLUID === '' ? Messages.analyticsExtensionsSettingsCreateConnectionDialogHeader() : Messages.analyticsExtensionsSettingsEditConnectionDialogHeader(),
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      shouldNotSetDefaultWidth: true,
      positioningBehavior: DialogPositioningBehavior.Draggable,
      shouldNotCloseOnGlassClick: true
    });
    return modalInstance.result;
  }

  function sendDelete(connectionLUID: string) {
    let result = confirmDeleteConnectionDialog(connectionLUID, deleteAnExtConnection);
    let resultOfQuestionableType = result as unknown as PromiseLike<IAnalyticsExtensionsSettings[]>;
    resultOfQuestionableType.then((result: IAnalyticsExtensionsSettings[]) => {
      let namedConnectionsList = Object.assign([], namedConnections);
      namedConnectionsList.forEach((connection, index) => {
        if (connection.connection_luid === connectionLUID) {
          namedConnectionsList.splice(index, 1);
        }
      });
      if (namedConnectionsList.length === 0) {
        let brief = defaultAnalyticsConnection.connection_brief;
        brief = {
          ...brief,
          connection_type: ConnectionTypes.SELECT
        };
        namedConnectionsList.push({
          ...defaultAnalyticsConnection,
          connection_brief: brief
        });
      }
      setNamedConnections(namedConnectionsList);
    });
  }

  function updateConnectionInfo(result: IAnalyticsExtConnectionSettings) {
    if (result) {
      const updateConnection = result;
      let connectionDisplay = '';
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
        connectionDisplay = ConnectionTypes.TABPY.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.RSERVE_HTTP.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE_HTTP.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
        connectionDisplay = ConnectionTypes.EINSTEIN.DISPLAY;
      }
      if (updateConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
        connectionDisplay = ConnectionTypes.GENERIC_API.DISPLAY;
      }
      updateConnection.connection_brief.connection_type = connectionDisplay;
      handleChangeAdaptorMultipleConnections(updateConnection);
    }
  }

  function handleChangeAdaptorMultipleConnections(updatedConnection: IAnalyticsExtConnectionSettings) {
    let connectionList = Object.assign([], namedConnections);
    let found = false;
    connectionList.forEach((connection: IAnalyticsExtConnectionSettings, index: number) => {
      if (connection.connection_luid === updatedConnection.connection_luid || connection.connection_luid === '') {
        found = true;
        connectionList[index] = updatedConnection;
      }
    });
    if (!found) {
      connectionList.push(updatedConnection);
    }
    setNamedConnections(connectionList);
  }

  let extensionsDisplay;
  let extensionRows = [];
  let waitingSpinner;

  if (namedConnections === undefined) {
    waitingSpinner = (
      <div className={AnalyticsExtensionsSettingsStyles.waitingForConnectionsSpinner}>
        <ActivitySpinnerWidget
          testId="analytics-extensions-loading"
          spinnerSize={ActivitySpinnerSize.Large}
        ></ActivitySpinnerWidget>
      </div>);
  } else {
    for (let namedConnection of namedConnections) {
      if (namedConnection.connection_brief.connection_type === ConnectionTypes.SELECT) {
        continue;
      }
      extensionRows.push(
        <tr className={AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
          <td className={AnalyticsExtensionsSettingsStyles.connectionCellIconNCFFOn}
            style={{
              backgroundImage: getConnectionTypeIcon(namedConnection.connection_brief.connection_type)
            }}>
          </td>
          <td className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}>
            <InputLabelWidget testId="anext_connection_name_value_nc_on">
              {namedConnection.connection_brief.connection_name}
            </InputLabelWidget>
          </td>
          <td className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}>
            <InputLabelWidget testId="anext_connection_type_value_nc_on" style={{ color: applyColor(DesignTokens.color.link.inline.default.default, '#1A699E') }}>
              {namedConnection.connection_brief.connection_type}
            </InputLabelWidget>
          </td>
          <td className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}>
            <ButtonWidget
              testId={`anext_connection_edit_button_nc_on${namedConnection.connection_luid}`}
              buttonType={ButtonType.Icon}
              id={`editConnectionButton${namedConnection.connection_luid}`}
              className={AnalyticsExtensionsSettingsStyles.getEditConnectionButton()}
              aria-label={Messages.analyticsExtensionsSettingsEdit()}
              handleClick={() => {
                showHybridDialog(getDialogProps(), namedConnection.connection_luid).then(
                  (result: IAnalyticsExtConnectionSettings) => {
                    updateConnectionInfo(result);
                  }
                );
              }}>
            </ButtonWidget>
          </td>
          <td className={AnalyticsExtensionsSettingsStyles.connectionCellNCFFOn}>
            <ButtonWidget
              testId={`anext_connection_delete_button_nc_on${namedConnection.connection_luid}`}
              buttonType={ButtonType.Icon}
              style={{color: DesignTokens.color.font.default.subtle}}
              id={`deleteConnectionButton${namedConnection.connection_luid}`}
              aria-label={Messages.analyticsExtensionsSettingsDelete()}
              onClick={() => sendDelete(namedConnection.connection_luid)}>
                <TrashBaseIcon size={18} />
            </ButtonWidget>
          </td>
        </tr>
      );
    }
  }

  if (extensionRows.length > 0) {
    extensionsDisplay =
      <table className={AnalyticsExtensionsSettingsStyles.connectionSettingsTableNCFFOn}>
        <tr className={AnalyticsExtensionsSettingsStyles.connectionRowNCFFOn}>
          <th className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} scope="col"
            style={{ width: '60px' }}>
          </th>
          <th className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} scope="col">
            <InputLabelWidget
              className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellLabel}
              testId="anext_connection_name_title_nc_on">
              {Messages.analyticsExtensionsSettingsConnectionName()}
            </InputLabelWidget>
          </th>
          <th className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} scope="col">
            <InputLabelWidget
              className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellLabel}
              testId="anext_connection_type_title_nc_on">
              {Messages.analyticsExtensionsSettingsConnectionType()}
            </InputLabelWidget>
          </th>
          <th className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} scope="col"></th>
          <th className={AnalyticsExtensionsSettingsStyles.connectionHeaderCellNCFFOn} scope="col"></th>
        </tr>
        {extensionRows}
      </table>;
  } else {
    extensionsDisplay =
      <div>
        <div className={AnalyticsExtensionsSettingsStyles.getNoConnectionIcon()}></div>
        <div className={AnalyticsExtensionsSettingsStyles.noConnectionTextLabel}>
          <InputLabelWidget testId="anext_no_connection_label_nc_on"
            className={AnalyticsExtensionsSettingsStyles.noConnectionText}>
            {Messages.analyticsExtensionsNoConnectionConfiguredEDFFOn()}
          </InputLabelWidget>
        </div>
      </div>;
  }

  return (
    <SettingsSection title={Messages.analyticsExtensionsSettings_header()} description={renderDescription(isSingleSiteOnPremServerSetup)}>
      <div className={AnalyticsExtensionsSettingsStyles.enableForSite}>
        <FormikCheckbox
          name="settings.analyticsExtensionsSiteSettings.enabled"
          testId="anext_site_enable_checkbox"
          label={isSingleSiteOnPremServerSetup ? Messages.analyticsExtensionsSettingsServer_checkbox() : Messages.analyticsExtensionsSettingsSite_checkbox()}
          disabled={!props.settings.analyticsExtensionsServerSettings?.enabled && !isSingleSiteOnPremServerSetup}
        />
        <div>
          <ButtonWidget
            testId="anext_create_new_connection_button"
            buttonType={ButtonType.Primary}
            id="addNewConnectionBtn"
            handleClick={() => {
              showHybridDialog(getDialogProps(), '').then(
                (result: IAnalyticsExtConnectionSettings) => {
                  updateConnectionInfo(result);
                }
              );
            }}>{Messages.analyticsExtensionsSettingsCreateConnection()}</ButtonWidget>
        </div>
      </div>
      <div>
        {waitingSpinner ? waitingSpinner : extensionsDisplay}
      </div>
    </SettingsSection>
  );
}

function getConnectionTypeDisplay(initInfoConnection: IAnalyticsExtConnectionSettings) {
  let connection = ConnectionTypes.SELECT;
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.TABPY.TAG) {
    connection = ConnectionTypes.TABPY.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.RSERVE.TAG) {
    connection = ConnectionTypes.RSERVE.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.RSERVE_HTTP.TAG) {
    connection = ConnectionTypes.RSERVE_HTTP.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.EINSTEIN.TAG) {
    connection = ConnectionTypes.EINSTEIN.DISPLAY;
  }
  if (initInfoConnection.connection_brief.connection_type === ConnectionTypes.GENERIC_API.TAG) {
    connection = ConnectionTypes.GENERIC_API.DISPLAY;
  }
  return connection;
}

function getConnectionTypeIcon(type: String) {
  switch (type) {
    case ConnectionTypes.RSERVE.DISPLAY:
    case ConnectionTypes.RSERVE_HTTP.DISPLAY: {
      return imgUrl(getAnalyticsExtensionConnectionIcon('RSERVE'));
    }
    case ConnectionTypes.TABPY.DISPLAY: {
      return imgUrl(getAnalyticsExtensionConnectionIcon('TABPY'));
    }
    case ConnectionTypes.EINSTEIN.DISPLAY: {
      return EINSTEIN_DISCOVERY_CELL_ICON_VAR;
    }
    case ConnectionTypes.GENERIC_API.DISPLAY: {
      return ANALYTICS_EXTENSION_API_CELL_ICON_VAR;
    }
    default: {
      return imgUrl(getAnalyticsExtensionConnectionIcon('SELECT'));
    }
  }
}

async function getAnalyticsConnections() {
  const result: IGetAnalyticsExtConnectionSettingsResult =
    await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsExtConnectionSettingsRequest());
  return result.connectionList;
}

function updateAnalyticsExtConnectionSettings(connectionSettings: IAnalyticsExtConnectionSettings) {
  // if this is the first time setting connection setting then need to call add instead of update
  if (connectionSettings.connection_luid === undefined || connectionSettings.connection_luid === '') {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new AddAnalyticsExtConnectionSettingsRequest(connectionSettings));
  } else {
    return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtConnectionSettingsRequest(connectionSettings));
  }
}

function deleteAnExtConnection(connectionId: IAnalyticsExtConnectionId) {
  return WebHttpClientWithErrorToastInstance.sendTagRequest(new DeleteAnalyticsExtConnectionSettingsRequest(connectionId));
}

function renderDescription(isSingleSiteOnPremServerSetup: boolean) {
  return (
    <div>
      {isSingleSiteOnPremServerSetup ? Messages.analyticsExtensionsSettingsServer_description() : Messages.analyticsExtensionsSettingsSite_description()}
      <TextLinkWidget testId={'analytics-extensions-server-settings-description'}
        target={'_blank'}
        className={learnMoreStyle}
        href={HelpService.getServerHelpUrlByCategory('analytics_extension')}>
        {Messages.learnMore()}
      </TextLinkWidget>
    </div>
  );
}

function AnExtSiteSettingRemoveActionStringBody(): JSX.Element {
  return (
    <div>
      <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderContainerEDFFOn}>
        <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeaderEDFFOn}>
          {Messages.analyticsExtensionsRemoveConnectionHeaderEDFFOn({ CONNECTION_TYPE: 'Connection' })}
        </div>
      </div>
      <h3>{Messages.analyticsExtensionsRemoveConnectionTextHeader()}</h3>
      <p>{Messages.analyticsExtensionsRemoveConnectionTextEDFFOn()}</p>
    </div>
  );
}

function confirmDeleteConnectionDialog(luid: string, request: (connectionId: IAnalyticsExtConnectionId) => Promise<IResult>) {
  const dialogInstance = openReactConfirmationDialog<void, IResult>({
    bodyComponent: AnExtSiteSettingRemoveActionStringBody,
    message: '',
    paramsForAction: undefined,
    actionName: Messages.analyticsExtensionsRemoveConnectionActionNameEDFFOn(),
    action: () => request({ connection_luid: luid }),
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px
  });
  return dialogInstance.result;
}

export const AnalyticsExtensionsSiteSectionSpec = componentRegistryEntry({
  name: 'AnalyticsExtensionsSiteSection',
  component: AnalyticsExtensionsSiteSection,
  onSubmit: async (settings: ExtensionsSettingsSubspaceState) => {
    await Promise.all([Sites.instance.updateAnalyticsExtensionsSiteSettings(settings.analyticsExtensionsSiteSettings)]);
  },
});
